<!--
  @Sidebar  Narrow side panel of a page in the prepare, manage and narrate modules, the width of the main navigation button.
  Items either call a function or open a flyout next to the sidebar (the help button is in the navigation bar).
  Actions of the page (e.g. creating new elements) are items marked as `primary`.
-->

<script module>
		let pending = null

		/** opens the flyout with the given ID on the next page shown (e.g. the map selection of a section) **/
		export const open_flyout_next = (flyoutID) => pending = flyoutID
</script>


<script>
		/** items: [{id, icon, label, title?, flyout?: heading of flyout, onclick?, active?, shown?, primary?: page action,
		 *           disabled?, help?: documentation topic if it differs from id}] **/
		let {items = [], open = $bindable(null), width = 320, flyout} = $props()

		if (pending) {
				open = pending
				pending = null
		}

		let shownItems = $derived(items.filter(item => item.shown ?? true))
		let openItem = $derived(shownItems.find(item => item.flyout && item.id === open))

		function click_item(item) {
				if (item.flyout)
					open = (open === item.id) ? null : item.id
				else
					item.onclick?.()
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<aside class="panel sidebar">
	{#each shownItems as item (item.id)}
		<button class="sideBtn" class:primary={item.primary} class:active={item.active || open === item.id} title={item.title ?? item.label}
		        disabled={item.disabled} data-help={item.help ?? item.id} onclick={() => click_item(item)}>
			<i class="fa icon-{item.icon}"></i>
			<span>{item.label}</span>
		</button>
	{/each}

	{#if openItem}
		<div class="flyout" style:width="{width}px">
			<div class="flyHead">
				<span class="card-kicker">{openItem.flyout}</span>
				<button class="flyClose fa icon-xmark" title="Schließen" aria-label="Schließen" onclick={() => open = null}></button>
			</div>
			<div class="flyBody">
				{@render flyout?.(openItem.id)}
			</div>
		</div>
	{/if}
</aside>


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	.sidebar
		grid-area: panel
		position: relative
		z-index: 5
		display: flex
		flex-direction: column
		align-items: center
		gap: 6px
		padding: 116px 0 16px		// below the main navigation button
		background: colors.$grey-15
		box-shadow: 1px 0 0 colors.$grey-20

	.sideBtn
		flex: none
		width: 92px
		display: flex
		flex-direction: column
		align-items: center
		gap: 4px
		margin: 0
		padding: 10px 4px 8px
		border: 0
		border-radius: 8px
		background: transparent
		color: colors.$grey-85
		font-size: 11px
		line-height: 1.2
		text-align: center
		cursor: pointer

		.fa
			font-size: 20px

		&:hover:not(:disabled)
			background: rgba(255, 255, 255, 0.07)
			color: colors.$white

		&:disabled
			opacity: 0.4
			cursor: default

		&.active
			background: colors.$primary-040-black
			color: colors.$white

		/* page action (e.g. create a new element): icon in a teal circle */
		&.primary .fa
			display: grid
			place-items: center
			width: 30px
			height: 30px
			border-radius: 50%
			border: 2px solid colors.$primary-100
			background: colors.$primary-040-black
			font-size: 13px

		&.primary:hover .fa
			background: colors.$primary-060-black

		&:focus-visible
			outline: 2px solid colors.$primary-100
			outline-offset: 2px

	/* — flyout next to the sidebar, below navigation bar and tabs — */
	.flyout
		position: fixed
		left: var(--menu-width)
		top: var(--sidebar-top, 102px)
		bottom: 0
		z-index: 8
		max-width: calc(100vw - var(--menu-width))
		max-height: 90vh
		display: flex
		flex-direction: column
		background: colors.$grey-15
		box-shadow: 0 0 0 1px colors.$grey-20, 12px 0 32px rgba(0, 0, 0, 0.6)
		overflow-y: auto
		scrollbar-width: none

	.flyHead
		display: flex
		align-items: center
		gap: 8px
		padding: 14px 16px 6px

	.flyClose
		margin: 0 0 0 auto
		padding: 4px 6px
		border: 0
		border-radius: 6px
		background: transparent
		color: colors.$grey-60
		font-size: 15px
		cursor: pointer

		&:hover
			color: colors.$white
			background: colors.$grey-20

	.flyBody
		display: flex
		flex-direction: column
		//gap: 12px
		padding: 4px 16px 20px
		font-size: 13px
		line-height: 1.5

</style>
